import { createFileRoute, Link } from "@tanstack/react-router";
import { NanaeHeader, NanaeFooter } from "@/components/qv3/NanaeShared";
import { LegalSection } from "@/components/qorum/LegalSection";
import { socialMeta, pageJsonLd } from "@/lib/seo-meta";

export const Route = createFileRoute("/confidentialite")({
  head: () => ({
    meta: [
      { title: "Politique de confidentialité — Hōjin" },
      {
        name: "description",
        content:
          "Politique de confidentialité Hōjin : données collectées, finalités, sous-traitants (LLM, Stripe, Supabase), transferts hors UE et droits RGPD.",
      },
      { name: "robots", content: "index, follow" },
      ...socialMeta({
        title: "Politique de confidentialité — Hōjin",
        description: "Politique de confidentialité Hōjin : données collectées, finalités, sous-traitants (LLM, Stripe, Supabase), transferts hors UE et droits RGPD.",
        url: "https://hojin.ai/confidentialite",
        image: "conseil",
        card: "summary",
      }),
    ],
    links: [{ rel: "canonical", href: "https://hojin.ai/confidentialite" }],
    scripts: pageJsonLd({
      path: "/confidentialite",
      name: "Politique de confidentialité — Hōjin",
      description: "Politique de confidentialité Hōjin : données collectées, finalités, sous-traitants, transferts hors UE et droits RGPD.",
      type: "WebPage",
      image: "conseil",
      breadcrumb: [{ name: "Confidentialité", path: "/confidentialite" }],
    }),
  }),
  component: ConfidentialitePage,
});

const SECTIONS = [
  {
    title: "Responsable de traitement",
    body: (
      <p>
        <strong>UDITEK SAS</strong>, éditeur de Hōjin. Contact pour toute
        question relative aux données personnelles : [EMAIL]. DPO : [NOM DPO si
        désigné, sinon « non désigné »].
      </p>
    ),
  },
  {
    title: "Données collectées",
    body: (
      <ul>
        <li>
          <strong>Compte</strong> : adresse email, mot de passe (stocké de
          manière hashée), date de création.
        </li>
        <li>
          <strong>Profil « Qui je suis »</strong> : informations renseignées
          par le Président pour contextualiser les délibérations.
        </li>
        <li>
          <strong>Contenu des délibérations</strong> : saisines (questions
          présentées au Conseil), verdicts rendus, échanges post-verdict.
        </li>
        <li>
          <strong>Données techniques</strong> : journaux d'accès, identifiants
          techniques, cookies (voir{" "}
          <Link to="/politique-cookies" style={{ color: "var(--gold-deep)" }}>
            politique cookies
          </Link>
          ).
        </li>
        <li>
          <strong>Paiement</strong> : traité directement par Stripe. Hōjin ne
          stocke aucun numéro de carte bancaire.
        </li>
      </ul>
    ),
  },
  {
    title: "Finalités et bases légales",
    body: (
      <ul>
        <li>Fourniture du service de délibération : exécution du contrat.</li>
        <li>Facturation et comptabilité : obligation légale.</li>
        <li>Mesure et monitoring d'erreurs : consentement.</li>
        <li>Sécurité du service et lutte contre la fraude : intérêt légitime.</li>
      </ul>
    ),
  },
  {
    title: "Sous-traitants et destinataires",
    body: (
      <>
        <p>
          <strong>Point critique :</strong> pour produire les verdicts, le
          contenu des saisines et certaines informations du profil sont
          transmis à des fournisseurs de modèles de langage tiers (LLM) :
        </p>
        <ul>
          <li>Anthropic (Claude)</li>
          <li>OpenAI (GPT)</li>
          <li>Google (Gemini)</li>
          <li>Mistral AI</li>
          <li>Perplexity</li>
          <li>DeepSeek</li>
          <li>Cohere</li>
        </ul>
        <p>Autres sous-traitants techniques :</p>
        <ul>
          <li>Stripe — traitement des paiements.</li>
          <li>Supabase — base de données et authentification.</li>
          <li>Railway — hébergement applicatif.</li>
          <li>Sentry — monitoring d'erreurs (uniquement sur consentement).</li>
          <li>
            APIs de recherche tierces (Tavily, Apify, etc.) — enrichissement
            documentaire des délibérations.
          </li>
        </ul>
        <p>[Liste à compléter exhaustivement par juriste.]</p>
      </>
    ),
  },
  {
    title: "Transferts hors Union européenne",
    body: (
      <p>
        Plusieurs sous-traitants (notamment les fournisseurs LLM et Stripe)
        sont établis hors Union européenne, en particulier aux États-Unis. Ces
        transferts sont encadrés par des clauses contractuelles types adoptées
        par la Commission européenne et/ou par le cadre d'adéquation
        UE/États-Unis (Data Privacy Framework) lorsque applicable. [À valider
        par juriste pour chaque fournisseur.]
      </p>
    ),
  },
  {
    title: "Durées de conservation",
    body: (
      <ul>
        <li>Données de compte : [À DÉFINIR] après la dernière connexion.</li>
        <li>Contenu des délibérations : [À DÉFINIR].</li>
        <li>Données de facturation : 10 ans (obligation comptable).</li>
        <li>Journaux techniques : [À DÉFINIR].</li>
      </ul>
    ),
  },
  {
    title: "Vos droits",
    body: (
      <>
        <p>
          Conformément au RGPD, vous disposez d'un droit d'accès, de
          rectification, d'effacement, de portabilité, d'opposition et de
          limitation du traitement de vos données. Ces droits s'exercent par
          écrit à [EMAIL].
        </p>
        <p>
          Vous disposez également du droit d'introduire une réclamation auprès
          de la Commission Nationale de l'Informatique et des Libertés (CNIL),
          3 place de Fontenoy — TSA 80715 — 75334 Paris Cedex 07.
        </p>
      </>
    ),
  },
  {
    title: "Cookies",
    body: (
      <p>
        L'utilisation des cookies est détaillée dans la{" "}
        <Link to="/politique-cookies" style={{ color: "var(--gold-deep)" }}>
          politique cookies
        </Link>
        .
      </p>
    ),
  },
];

export function ConfidentialitePage() {
  return (
    <div className="nanae-page" style={{ background: "var(--ivory)", minHeight: "100vh" }}>
      <NanaeHeader title="Politique de confidentialité" variant="public" />
      <section
        aria-label="Politique de confidentialité"
        style={{ maxWidth: 720, margin: "0 auto", padding: "64px 32px 80px" }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 20,
          }}
        >
          <span
            aria-hidden
            style={{ width: 24, height: 0.5, background: "var(--accent-cramoisi)", display: "inline-block" }}
          />
          <span
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: 10,
              color: "var(--gold-deep)",
              letterSpacing: "3px",
              textTransform: "uppercase",
            }}
          >
            Confidentialité · Volume MMXXVI
          </span>
        </div>
        <h1
          style={{
            fontFamily: '"Anton", sans-serif',
            fontWeight: 400,
            fontSize: 64,
            color: "var(--charcoal)",
            letterSpacing: "-1.5px",
            textTransform: "uppercase",
            lineHeight: 0.95,
            margin: "0 0 32px",
          }}
        >
          Confidentialité
        </h1>
        <LegalSection articles={SECTIONS} />
      </section>
      <NanaeFooter variant="full" />
    </div>
  );
}
